/*
  * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
  * SPDX-License-Identifier: MIT
  *
  * Licensed under the MIT License. See the LICENSE accompanying this file
  * for the specific language governing permissions and limitations under
  * the License.
  */

// サブドメイン間で共有するCookie発行に使用するドメイン (例: ".example.com")。
// 未設定の場合は共有Cookieの発行・削除を行わない (warn出力)。
var SHARED_COOKIE_DOMAIN = process.env.REACT_APP_SHARED_COOKIE_DOMAIN || "";

// ログイン状態をサブドメイン間に通知するための共有Cookie名。
var SHARED_LOGIN_COOKIE_NAME = process.env.REACT_APP_SHARED_LOGIN_COOKIE_NAME || "";

// ログアウト状態をサブドメイン間に通知するための共有Cookie名。
var SHARED_LOGOUT_COOKIE_NAME = process.env.REACT_APP_SHARED_LOGOUT_COOKIE_NAME || "";

// 共有Cookieの有効期限 (秒)。1年。
var SHARED_COOKIE_MAX_AGE_SEC = 365 * 24 * 60 * 60;

export function getCookie(name) {
	var nameEQ = name + "=";
	var ca = document.cookie.split(';');
	for (var i = 0; i < ca.length; i++) {
		var c = ca[i];
		while (c.charAt(0) === ' ') c = c.substring(1, c.length);
		if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
	}
	return null;
}

export function setCookie(name, value, expiry) {
	var expires = "";
	if (expiry) {
		var date = new Date(expiry * 1000);
		expires = "; expires=" + date.toUTCString();
	}
	document.cookie = name + "=" + (value || "") + expires + "; path=/";
}

export function eraseCookie(name) {
	document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

/**
 * サブドメイン間で共有するCookieを発行する。
 *
 * - domain: 環境変数 REACT_APP_SHARED_COOKIE_DOMAIN から読み込む
 * - Secure: true 固定
 * - SameSite: Lax 固定
 *
 * 環境変数が未設定の場合は警告を出力し、Cookieは発行しない。
 *
 * @param {string} name   Cookie名
 * @param {string} value  値
 * @param {number} [expiry] 有効期限 (UNIX秒)。falsyの場合はセッションCookie。
 */
export function setSharedCookie(name, value, expiry) {
	if (!SHARED_COOKIE_DOMAIN) {
		console.warn("setSharedCookie: REACT_APP_SHARED_COOKIE_DOMAIN is not set. Cookie was not issued: " + name);
		return;
	}
	var expires = "";
	if (expiry) {
		var date = new Date(expiry * 1000);
		expires = "; expires=" + date.toUTCString();
	}
	document.cookie = name + "=" + (value || "") + expires
		+ "; path=/"
		+ "; domain=" + SHARED_COOKIE_DOMAIN
		+ "; Secure"
		+ "; SameSite=Lax";
}

/**
 * setSharedCookieで発行したCookieを削除する。
 * 発行時と同じdomain (環境変数 REACT_APP_SHARED_COOKIE_DOMAIN) を指定する必要があるため、
 * 通常のeraseCookieでは削除できないので注意。
 *
 * @param {string} name Cookie名
 */
export function eraseSharedCookie(name) {
	if (!SHARED_COOKIE_DOMAIN) {
		console.warn("eraseSharedCookie: REACT_APP_SHARED_COOKIE_DOMAIN is not set. Cookie was not erased: " + name);
		return;
	}
	document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT'
		+ '; domain=' + SHARED_COOKIE_DOMAIN
		+ '; Secure'
		+ '; SameSite=Lax;';
}

/**
 * サブドメイン共有のログアウトCookieを削除する。
 * registration_token フロー開始時やログイン成功時に使用する。
 */
export function eraseSharedLogoutCookie() {
	if (SHARED_LOGOUT_COOKIE_NAME) {
		if (getCookie(SHARED_LOGOUT_COOKIE_NAME)) {
			eraseSharedCookie(SHARED_LOGOUT_COOKIE_NAME);
		}
	} else {
		console.warn("eraseSharedLogoutCookie: REACT_APP_SHARED_LOGOUT_COOKIE_NAME is not set.");
	}
}

/**
 * ログイン時にサブドメイン共有Cookieを更新する。
 * - REACT_APP_SHARED_LOGIN_COOKIE_NAME のCookieを発行 (値: タイムスタンプ, 有効期限: 1年)
 * - REACT_APP_SHARED_LOGOUT_COOKIE_NAME のCookieが存在すれば削除
 */
export function setSharedCookieOnLogin() {
	if (SHARED_LOGIN_COOKIE_NAME) {
		var expiry = Math.floor(Date.now() / 1000) + SHARED_COOKIE_MAX_AGE_SEC;
		setSharedCookie(SHARED_LOGIN_COOKIE_NAME, Date.now().toString(), expiry);
	} else {
		console.warn("setSharedCookieOnLogin: REACT_APP_SHARED_LOGIN_COOKIE_NAME is not set.");
	}

	eraseSharedLogoutCookie();
}

/**
 * ログアウト時にサブドメイン共有Cookieを更新する。
 * - REACT_APP_SHARED_LOGIN_COOKIE_NAME のCookieを削除
 * - REACT_APP_SHARED_LOGOUT_COOKIE_NAME のCookieが存在しなければ発行 (値: タイムスタンプ, 有効期限: 1年)
 */
export function setSharedCookieOnLogout() {
	if (SHARED_LOGIN_COOKIE_NAME) {
		eraseSharedCookie(SHARED_LOGIN_COOKIE_NAME);
	} else {
		console.warn("setSharedCookieOnLogout: REACT_APP_SHARED_LOGIN_COOKIE_NAME is not set.");
	}

	if (SHARED_LOGOUT_COOKIE_NAME) {
		if (!getCookie(SHARED_LOGOUT_COOKIE_NAME)) {
			var expiry = Math.floor(Date.now() / 1000) + SHARED_COOKIE_MAX_AGE_SEC;
			setSharedCookie(SHARED_LOGOUT_COOKIE_NAME, Date.now().toString(), expiry);
		}
	} else {
		console.warn("setSharedCookieOnLogout: REACT_APP_SHARED_LOGOUT_COOKIE_NAME is not set.");
	}
}
